import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";
import { CustomAddressAutocomplete } from "../../shared/CustomAddressAutoComplete";

const HOUSING_STATUS_OPTIONS = ["Own", "Rent", "With Relatives", "With Friends"];
const SUFFIX_OPTIONS = ["Jr.", "Sr.", "II", "III", "IV", "V"];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Separated"];

/**
 * PD-6 Milestone 2 — personal path Step 2 "Residence".
 *
 * Merges personal/Suffix.js (suffix + marital status — sat immediately
 * before ResidentialAddress in the old chain, so it belongs here),
 * personal/ResidentialAddress.js (residential + mailing address +
 * "plan to register vehicle" yes/no) and personal/Living.js (time at
 * address, monthly housing payment, housing status) into one step.
 *
 * Every field keeps its own individual `partial-application` autosave POST
 * call with the exact same payload shape as its original standalone screen.
 */
const StepResidence = forwardRef(({ goNext, goBack, isFirstStep, footNote }, ref) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const [suffix, setSuffix] = useState(state?.questions?.personal?.suffix ?? "");
  const [maritalStatus, setMaritalStatus] = useState(
    state?.questions?.personal?.maritalStatus ?? ""
  );

  const [residentialAddress, setResidentialAddress] = useState(
    state?.questions?.personal?.residentialAddress ?? ""
  );
  const [residentialAddressComponents, setResidentialAddressComponents] = useState(
    JSON.parse(localStorage.getItem("residentialComponentsMazo")) ?? []
  );
  const [residentialAddressError, setResidentialAddressError] = useState("");
  const [residentialUnitNumber, setResidentialUnitNumber] = useState(
    state?.questions?.personal?.residentialUnitNumber ?? ""
  );

  const [mailingAddress, setMailingAddress] = useState(
    state?.questions?.personal?.mailingAddress ?? ""
  );
  const [mailingAddressComponents, setMailingAddressComponents] = useState(
    JSON.parse(localStorage.getItem("mailingComponentsMazo")) ?? []
  );
  const [mailingAddressError, setMailingAddressError] = useState("");
  const [mailingUnitNumber, setMailingUnitNumber] = useState(
    state?.questions?.personal?.mailingUnitNumber ?? ""
  );
  // Restored ticked when the saved mailing address already mirrors the
  // residential one (Back, or a resumed application), same as
  // StepBusiness.js's "Same as company address".
  const [sameAsResidential, setSameAsResidential] = useState(
    () =>
      Boolean(state?.questions?.personal?.residentialAddress) &&
      state?.questions?.personal?.mailingAddress ===
        state?.questions?.personal?.residentialAddress &&
      (state?.questions?.personal?.mailingUnitNumber ?? "") ===
        (state?.questions?.personal?.residentialUnitNumber ?? "")
  );

  const [planToRegisterVehicle, setPlanToRegisterVehicle] = useState(
    state?.questions?.personal?.isPlanToRegisterVehicle ?? true
  );

  const [livingYears, setLivingYears] = useState(
    state?.questions?.personal?.livingYears ?? ""
  );
  const [livingMonths, setLivingMonths] = useState(
    state?.questions?.personal?.livingMonths ?? ""
  );
  const [livingMonthlyPayment, setLivingMonthlyPayment] = useState(
    state?.questions?.personal?.livingMonthlyPayment
      ? parseInt(state?.questions?.personal?.livingMonthlyPayment)?.toLocaleString()
      : ""
  );
  const [livingStatus, setLivingStatus] = useState(
    state?.questions?.personal?.livingStatus ?? ""
  );

  // Selecting a suggestion fills in the address text immediately, but its
  // structured components (required for validation) only land once the
  // place-details lookup resolves. Track in-flight lookups so Continue can't
  // race ahead of them.
  const [pendingAddressLookups, setPendingAddressLookups] = useState(0);
  const handleAddressResolvingChange = (isResolving) => {
    setPendingAddressLookups((count) => count + (isResolving ? 1 : -1));
  };

  const [errors, setErrors] = useState({});

  // "Just saved now": every field on this step lands in global state as the
  // borrower types/selects, not only once they click Continue — so
  // navigating Back then Continue again restores whatever was filled in
  // here, instead of losing it because this component's local state was
  // discarded on unmount. (Address autocomplete *components*, needed for
  // validation, already survive navigation independently via localStorage —
  // this only needs to cover the plain search-string fields.)
  useEffect(() => {
    const parsedLivingMonthlyPayment = parseInt(String(livingMonthlyPayment).replace(/,/g, ""));
    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        suffix,
        maritalStatus,
        residentialAddress,
        residentialUnitNumber,
        mailingAddress,
        mailingUnitNumber,
        isPlanToRegisterVehicle: planToRegisterVehicle,
        livingYears,
        livingMonths,
        livingMonthlyPayment: isNaN(parsedLivingMonthlyPayment) ? null : parsedLivingMonthlyPayment,
        livingStatus,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    suffix,
    maritalStatus,
    residentialAddress,
    residentialUnitNumber,
    mailingAddress,
    mailingUnitNumber,
    planToRegisterVehicle,
    livingYears,
    livingMonths,
    livingMonthlyPayment,
    livingStatus,
  ]);

  const handleSameAsResidentialChange = (e) => {
    const checked = e.target.checked;
    setSameAsResidential(checked);
    if (checked) {
      setMailingAddress(residentialAddress);
      setMailingAddressComponents(residentialAddressComponents);
      setMailingAddressError("");
      setMailingUnitNumber(residentialUnitNumber);
      // Mirror what a real selection does — persist the components so a
      // remount (e.g. Continue then Back) doesn't lose them, which would
      // otherwise fail validation on a field the borrower can't edit.
      localStorage.setItem(
        "mailingComponentsMazo",
        JSON.stringify(residentialAddressComponents)
      );
    } else {
      setMailingAddress("");
      setMailingAddressComponents([]);
      setMailingUnitNumber("");
      localStorage.removeItem("mailingComponentsMazo");
    }
  };

  const validate = () => {
    const validationErrors = {};
    if (!maritalStatus) {
      validationErrors.maritalStatus = "Please select an option.";
    }
    if (!residentialAddress || residentialAddressComponents.length === 0) {
      validationErrors.residentialAddress = "Please select your address from the list.";
    } else if (helpers.extractAddressComponents(residentialAddressComponents) === false) {
      validationErrors.residentialAddress =
        "Please enter a valid address with state, zip code, city and country.";
    }
    if (mailingAddress && mailingAddressComponents.length === 0) {
      validationErrors.mailingAddress = "Please select your mailing address from the list.";
    }
    if (!livingYears) validationErrors.livingYears = "This field is required.";
    if (!livingMonths) validationErrors.livingMonths = "This field is required.";
    if (livingMonthlyPayment === "" || livingMonthlyPayment === null) {
      validationErrors.livingMonthlyPayment = "This field is required.";
    }
    return validationErrors;
  };

  useImperativeHandle(ref, () => ({
    validateForSaveLater: () => {
      setResidentialAddressError("");
      setMailingAddressError("");
      const validationErrors = validate();
      setErrors(validationErrors);
      setSubmitAttempted(true);
      if (validationErrors.residentialAddress) {
        setResidentialAddressError(validationErrors.residentialAddress);
      }
      if (validationErrors.mailingAddress) {
        setMailingAddressError(validationErrors.mailingAddress);
      }
      return Object.keys(validationErrors).length === 0;
    },
  }));

  // Re-run validation live once the borrower has tried to submit once, so a
  // field's error clears as soon as it becomes valid instead of waiting for
  // another Continue press.
  useEffect(() => {
    if (!submitAttempted) return;
    const validationErrors = validate();
    setErrors(validationErrors);
    setResidentialAddressError(validationErrors.residentialAddress || "");
    setMailingAddressError(validationErrors.mailingAddress || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submitAttempted,
    maritalStatus,
    residentialAddress,
    residentialAddressComponents,
    mailingAddress,
    mailingAddressComponents,
    livingYears,
    livingMonths,
    livingMonthlyPayment,
  ]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (pendingAddressLookups > 0) return;
    setSubmitAttempted(true);
    setServerError("");
    setResidentialAddressError("");
    setMailingAddressError("");

    const validationErrors = validate();
    setErrors(validationErrors);
    if (validationErrors.residentialAddress) {
      setResidentialAddressError(validationErrors.residentialAddress);
    }
    if (validationErrors.mailingAddress) {
      setMailingAddressError(validationErrors.mailingAddress);
    }
    if (Object.keys(validationErrors).length > 0) return;

    const parsedLivingMonthlyPayment = parseInt(String(livingMonthlyPayment).replace(/,/g, ""));

    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        suffix,
        maritalStatus,
        residentialAddress,
        residentialUnitNumber,
        mailingAddress,
        mailingUnitNumber,
        isPlanToRegisterVehicle: planToRegisterVehicle,
        livingYears,
        livingMonths,
        livingMonthlyPayment: isNaN(parsedLivingMonthlyPayment) ? null : parsedLivingMonthlyPayment,
        livingStatus,
      },
    });

    const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;
    const residential = helpers.extractAddressComponents(
      residentialAddressComponents,
      { state: "short", route: "short" },
      residentialUnitNumber
    );
    const mailing = helpers.extractAddressComponents(
      mailingAddressComponents,
      { state: "short", route: "short" },
      mailingUnitNumber
    );

    setShowLoader(true);
    try {
      // Preserves the existing double-`body`-wrapped POST from
      // ResidentialAddress.js byte-for-byte.
      const body = { personal_is_plan_to_register_vehicle: planToRegisterVehicle };
      if (residential !== false) body.personal_home_address = residential;
      if (mailing !== false) body.personal_mailing_address = mailing;

      // These are independent fields, so fire them concurrently instead of
      // one round trip at a time.
      await Promise.all([
        axios.post(endpoint, {
          personal_suffix: suffix,
          personal_marital_status: maritalStatus,
        }),
        axios.post(endpoint, { body }),
        axios.post(endpoint, {
          personal_living_years: livingYears,
          personal_living_months: livingMonths,
          personal_monthly_payment: isNaN(parsedLivingMonthlyPayment)
            ? null
            : parsedLivingMonthlyPayment,
          personal_living_status: livingStatus,
        }),
      ]);
      goNext();
    } catch (error) {
      setServerError(
        error?.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      <h2 className="pd6-step__title">Where you live</h2>
      <p className="pd6-step__helper">
        Your address determines titling, so we ask where the equipment will be registered.
      </p>

      <Form noValidate onSubmit={handleContinue} className="pd6-form">
        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepResidenceSuffix">
            <Form.Label>Suffix</Form.Label>
            <div className="pd6-chip-group">
              {SUFFIX_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip ${suffix === option ? "pd6-chip--selected" : ""}`}
                  onClick={() => setSuffix(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </Form.Group>
          <Form.Group className="pd6-field" controlId="stepResidenceMaritalStatus">
            <Form.Label>
              Marital status<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              {MARITAL_STATUS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip ${maritalStatus === option ? "pd6-chip--selected" : ""}`}
                  onClick={() => setMaritalStatus(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            {submitAttempted && errors.maritalStatus && (
              <div className="pd6-field-error">{errors.maritalStatus}</div>
            )}
          </Form.Group>
        </div>

        <Form.Group className="pd6-field" controlId="stepResidenceAddress">
          <CustomAddressAutocomplete
            label="Residential address"
            addressSearchQuery={residentialAddress}
            // Keep a mirrored (and therefore locked) mailing address in
            // sync, same as StepBusiness.js.
            setAddressSearchQuery={(value) => {
              setResidentialAddress(value);
              if (sameAsResidential) setMailingAddress(value);
            }}
            setAddressComponents={(components) => {
              setResidentialAddressComponents(components);
              if (sameAsResidential) {
                setMailingAddressComponents(components);
                localStorage.setItem("mailingComponentsMazo", JSON.stringify(components));
              }
            }}
            addressValidationError={submitAttempted ? residentialAddressError : ""}
            setAddressValidationError={setResidentialAddressError}
            placeholder="residential address"
            localStorageLabel="residentialComponentsMazo"
            isRequired={true}
            onResolvingChange={handleAddressResolvingChange}
          />
          <Form.Control
            type="text"
            className="pd6-address-unit-input"
            placeholder="Apt, suite, or unit (optional)"
            autoComplete="off"
            value={residentialUnitNumber}
            onChange={(e) => {
              setResidentialUnitNumber(e.target.value);
              if (sameAsResidential) setMailingUnitNumber(e.target.value);
            }}
          />
        </Form.Group>

        <Form.Group className="pd6-field" controlId="stepResidenceMailingAddress">
          <CustomAddressAutocomplete
            label="Mailing address"
            addressSearchQuery={mailingAddress}
            setAddressSearchQuery={setMailingAddress}
            setAddressComponents={setMailingAddressComponents}
            addressValidationError={submitAttempted ? mailingAddressError : ""}
            setAddressValidationError={setMailingAddressError}
            placeholder="mailing address"
            localStorageLabel="mailingComponentsMazo"
            isRequired={false}
            onResolvingChange={handleAddressResolvingChange}
            disabled={sameAsResidential}
          />
          <Form.Control
            type="text"
            className="pd6-address-unit-input"
            placeholder="Apt, suite, or unit (optional)"
            autoComplete="off"
            value={mailingUnitNumber}
            onChange={(e) => setMailingUnitNumber(e.target.value)}
            disabled={sameAsResidential}
          />
          <div className="pd6-checkbox-row">
            <input
              type="checkbox"
              id="stepResidenceSameAddress"
              checked={sameAsResidential}
              disabled={!residentialAddress}
              onChange={handleSameAsResidentialChange}
            />
            <label htmlFor="stepResidenceSameAddress">Same as residential address</label>
          </div>
        </Form.Group>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepResidencePlanToRegister">
            <Form.Label>
              Registering the equipment here?
              <span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-chip-group">
              <button
                type="button"
                className={`pd6-chip ${planToRegisterVehicle ? "pd6-chip--selected" : ""}`}
                onClick={() => setPlanToRegisterVehicle(true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`pd6-chip ${!planToRegisterVehicle ? "pd6-chip--selected" : ""}`}
                onClick={() => setPlanToRegisterVehicle(false)}
              >
                No
              </button>
            </div>
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepResidenceDuration">
            <Form.Label>
              How long at this address<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-field-row pd6-field-row--tight">
              <div>
                <Form.Control
                  type="text"
                  placeholder="Years"
                  autoComplete="off"
                  value={livingYears}
                  onChange={(e) => setLivingYears(e.target.value)}
                  isInvalid={submitAttempted && Boolean(errors.livingYears)}
                />
                <Form.Control.Feedback type="invalid">{errors.livingYears}</Form.Control.Feedback>
              </div>
              <div>
                <Form.Control
                  type="text"
                  placeholder="Months"
                  autoComplete="off"
                  value={livingMonths}
                  onChange={(e) => setLivingMonths(e.target.value)}
                  isInvalid={submitAttempted && Boolean(errors.livingMonths)}
                />
                <Form.Control.Feedback type="invalid">{errors.livingMonths}</Form.Control.Feedback>
              </div>
            </div>
          </Form.Group>
        </div>

        <div className="pd6-field-row">
          <Form.Group className="pd6-field" controlId="stepResidenceMonthlyPayment">
            <Form.Label>
              Monthly housing payment<span className="pd6-required">*</span>
            </Form.Label>
            <div className="pd6-currency-input">
              <span className="pd6-currency-input__prefix">$</span>
              <Form.Control
                type="text"
                placeholder="0"
                value={livingMonthlyPayment}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
                  const parsed = parseInt(cleaned);
                  setLivingMonthlyPayment(isNaN(parsed) ? "" : parsed.toLocaleString());
                }}
                isInvalid={submitAttempted && Boolean(errors.livingMonthlyPayment)}
              />
            </div>
            {submitAttempted && errors.livingMonthlyPayment && (
              <div className="pd6-field-error">{errors.livingMonthlyPayment}</div>
            )}
          </Form.Group>

          <Form.Group className="pd6-field" controlId="stepResidenceHousingStatus">
            <Form.Label>Housing status</Form.Label>
            <div className="pd6-chip-group pd6-chip-group--bands">
              {HOUSING_STATUS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`pd6-chip pd6-chip--band ${
                    livingStatus === option ? "pd6-chip--selected" : ""
                  }`}
                  onClick={() => setLivingStatus(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </Form.Group>
        </div>

        {serverError && <div className="pd6-field-error mb-3">{serverError}</div>}

        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="pd6-form-summary-error">
            {Object.keys(errors).length} field{Object.keys(errors).length === 1 ? "" : "s"} still
            need{Object.keys(errors).length === 1 ? "s" : ""} an answer.
          </div>
        )}

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button
              type="submit"
              className="pd6-btn pd6-btn--primary"
              disabled={showLoader || pendingAddressLookups > 0}
            >
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Continue</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>
    </div>
  );
});

export default StepResidence;
